import { MANIA_LANE_COUNT, type ManiaLane } from '@shared/maniaNote';
import { comboTier, withAlpha } from '../../piano/render/pianoRenderColour';
import type { RenderQuality } from '../../rendering/renderQuality';
import type { GuitarLayout } from '../guitarPlayfieldGeometry';
import {
  BEAT_PULSE_DECAY_POWER,
  COMBO_BLOOM_PALETTE,
  HORIZON_GLOW_BASE_ALPHA,
  HORIZON_GLOW_RADIUS_FRAC,
  HORIZON_GLOW_TIER_BOOST,
  LANE_GAP_PX,
  LANE_GEM_ACCENTS,
  LANE_GEM_COLOURS,
  PRESS_CHEVRON_ALPHA,
  PRESS_CHEVRON_COUNT,
  PRESS_CHEVRON_CYCLE_S,
  SPEED_LINE_ALPHA,
  SPEED_LINE_COUNT_PER_LANE,
  SPEED_LINE_THICKNESS_PX,
  SPEED_LINE_TRAVEL_S,
  VIGNETTE_ALPHA,
} from './guitarRenderConstants';

/**
 * Ambient scene chrome that sits "outside" the gem highway: the
 * scrolling speed-line stripes that give the eye persistent forward
 * motion, the horizon glow at the far horizon that reacts to combo
 * tier, and the vignette that softly darkens the canvas corners to
 * focus attention on the playfield.
 *
 * Pulled into a separate module so `guitarDrawScene.ts` stays scoped
 * to "highway + strum bar + lane flashes" while still being able to
 * compose a "stadium light show" feel via these atmosphere layers.
 *
 * Per the AI guidelines: every gradient is cached behind a
 * dimension-keyed cache, every effect respects the `reducedMotion`
 * and `RenderQuality` flags, and no allocations happen in the
 * per-frame hot path beyond a phase increment.
 */
export class GuitarSceneAtmosphere {
  private speedLinePhase = 0;
  /**
   * Independent phase for the pressed-lane upward chevron stream so
   * the press chevrons flow against the speed-line gems (toward the
   * horizon) without the eye reading them as the same effect.
   */
  private pressChevronPhase = 0;
  private horizonGradient: CanvasGradient | null = null;
  private horizonKey = '';
  private vignetteGradient: CanvasGradient | null = null;
  private vignetteKey = '';

  reset(): void {
    this.speedLinePhase = 0;
    this.pressChevronPhase = 0;
  }

  advance(dt: number): void {
    if (dt <= 0) {
      return;
    }
    if (SPEED_LINE_TRAVEL_S > 0) {
      this.speedLinePhase = (this.speedLinePhase + dt / SPEED_LINE_TRAVEL_S) % 1;
    }
    if (PRESS_CHEVRON_CYCLE_S > 0) {
      this.pressChevronPhase = (this.pressChevronPhase + dt / PRESS_CHEVRON_CYCLE_S) % 1;
    }
  }

  /**
   * Faint vertical stripes drifting down each lane from the back wall
   * to the strum bar, with perspective-scaled thickness so the eye
   * reads forward motion at all times. Skipped on the low quality
   * tier (the per-stripe path-stroke cost piles up on phones) and
   * attenuated under reduced motion.
   *
   * Each lane runs {@link SPEED_LINE_COUNT_PER_LANE} stripes phase-
   * offset by `i / N` so the lane never goes "dark" between sweeps.
   */
  drawSpeedLines(
    ctx: CanvasRenderingContext2D,
    layout: GuitarLayout,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    if (reducedMotion || !quality.decorations) {
      return;
    }
    const span = layout.strumBarY - layout.highwayTopY;
    if (span <= 0) {
      return;
    }
    const topGap = LANE_GAP_PX * (layout.topStackWidth / layout.bottomStackWidth);
    const topStackLeft = layout.centreX - layout.topStackWidth / 2;
    const bottomStackLeft = layout.centreX - layout.bottomStackWidth / 2;

    const widthFrac = 0.78;
    ctx.save();
    for (let lane = 0; lane < MANIA_LANE_COUNT; lane += 1) {
      const accent = LANE_GEM_ACCENTS[lane as ManiaLane];
      const tlX = topStackLeft + lane * (layout.topLaneWidth + topGap);
      const trX = tlX + layout.topLaneWidth;
      const blX = bottomStackLeft + lane * (layout.bottomLaneWidth + LANE_GAP_PX);
      const brX = blX + layout.bottomLaneWidth;
      for (let i = 0; i < SPEED_LINE_COUNT_PER_LANE; i += 1) {
        const progress = (this.speedLinePhase + i / SPEED_LINE_COUNT_PER_LANE) % 1;
        // Fade-in at the back, fade-out at the very front so the
        // stripe never abruptly pops in or clips through the strum bar.
        const lifeAlpha =
          progress < 0.12
            ? progress / 0.12
            : progress > 0.92
              ? (1 - progress) / 0.08
              : 1;
        const yTop = layout.highwayTopY + span * progress;
        const thickness = SPEED_LINE_THICKNESS_PX * (0.45 + 0.55 * progress);
        const lxAtP = tlX + (blX - tlX) * progress;
        const rxAtP = trX + (brX - trX) * progress;
        const stripeW = (rxAtP - lxAtP) * widthFrac;
        const cx = (lxAtP + rxAtP) / 2;
        ctx.fillStyle = withAlpha(accent, SPEED_LINE_ALPHA * progress * lifeAlpha);
        ctx.fillRect(cx - stripeW / 2, yTop, stripeW, thickness);
      }
    }
    ctx.restore();
  }

  /**
   * Per-lane upward chevron stream rendered when a lane is currently
   * pressed. Three arrows per pressed lane, phase-staggered, drifting
   * from the strum bar toward the horizon — opposite to gem flow so
   * the eye reads them as "your tap energy returning upstream"
   * rather than just another approaching object. Skipped on low
   * tier (extra path-fill cost) and under reduced motion.
   *
   * Each chevron is lane-coloured and sized to perspective so it
   * shrinks as it climbs the trapezoid. Sine-bell alpha keeps the
   * head + tail of the stream soft so the chevrons never abruptly
   * appear or clip at the highway edges.
   */
  drawPressChevrons(
    ctx: CanvasRenderingContext2D,
    layout: GuitarLayout,
    pressedLanes: ReadonlySet<ManiaLane>,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    if (reducedMotion || !quality.decorations || pressedLanes.size === 0) {
      return;
    }
    const span = layout.strumBarY - layout.highwayTopY;
    if (span <= 0) {
      return;
    }
    const topGap = LANE_GAP_PX * (layout.topStackWidth / layout.bottomStackWidth);
    const topStackLeft = layout.centreX - layout.topStackWidth / 2;
    const bottomStackLeft = layout.centreX - layout.bottomStackWidth / 2;
    ctx.save();
    for (const lane of pressedLanes) {
      const baseColour = LANE_GEM_COLOURS[lane];
      const tlX = topStackLeft + lane * (layout.topLaneWidth + topGap);
      const trX = tlX + layout.topLaneWidth;
      const blX = bottomStackLeft + lane * (layout.bottomLaneWidth + LANE_GAP_PX);
      const brX = blX + layout.bottomLaneWidth;
      for (let i = 0; i < PRESS_CHEVRON_COUNT; i += 1) {
        // Travel runs strum bar → horizon, so progress = 1 at the
        // strum bar and 0 at the back. Phase ticks forward over time
        // and we subtract from 1 to invert it visually.
        const raw = (this.pressChevronPhase + i / PRESS_CHEVRON_COUNT) % 1;
        const progress = 1 - raw;
        const yCentre = layout.highwayTopY + span * progress;
        const lxAtP = tlX + (blX - tlX) * progress;
        const rxAtP = trX + (brX - trX) * progress;
        const laneW = rxAtP - lxAtP;
        const chevronW = laneW * 0.42;
        const chevronH = laneW * 0.18;
        const cx = (lxAtP + rxAtP) / 2;
        const lifeAlpha = Math.sin(raw * Math.PI);
        const alpha = lifeAlpha * PRESS_CHEVRON_ALPHA;
        if (alpha <= 0.02) {
          continue;
        }
        ctx.fillStyle = withAlpha(baseColour, alpha);
        ctx.beginPath();
        ctx.moveTo(cx - chevronW / 2, yCentre + chevronH / 2);
        ctx.lineTo(cx + chevronW / 2, yCentre + chevronH / 2);
        ctx.lineTo(cx, yCentre - chevronH / 2);
        ctx.closePath();
        ctx.fill();
      }
    }
    ctx.restore();
  }

  /**
   * Radial bloom anchored at the highway's far horizon — the visual
   * "back wall" the gems emerge from. Brightness scales with combo
   * tier so a long perfect streak literally lights the back of the
   * stage. Cached per-dimension+colour so the radial-gradient cost
   * only pays on resize / tier change.
   */
  drawHorizonGlow(
    ctx: CanvasRenderingContext2D,
    layout: GuitarLayout,
    combo: number,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    if (!quality.backgroundBloom) {
      return;
    }
    const tier = comboTier(combo);
    const colour = COMBO_BLOOM_PALETTE[tier];
    const radius = Math.max(40, layout.bottomStackWidth * HORIZON_GLOW_RADIUS_FRAC);
    const cx = layout.centreX;
    const cy = layout.highwayTopY;
    const alpha =
      HORIZON_GLOW_BASE_ALPHA +
      HORIZON_GLOW_TIER_BOOST * tier +
      (reducedMotion ? 0 : Math.min(0.12, combo / 280));
    const key = `${Math.round(cx)}|${Math.round(cy)}|${Math.round(radius)}|${colour}`;
    if (!this.horizonGradient || this.horizonKey !== key) {
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      grad.addColorStop(0, withAlpha(colour, 1));
      grad.addColorStop(0.55, withAlpha(colour, 0.35));
      grad.addColorStop(1, withAlpha(colour, 0));
      this.horizonGradient = grad;
      this.horizonKey = key;
    }
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = this.horizonGradient;
    ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
    ctx.restore();
  }

  /**
   * Soft darkening at the canvas corners so the eye is funnelled
   * toward the highway. Painted over the rest of the scene as the
   * last atmospheric pass. Cached per-canvas-size; reused across
   * frames until the canvas resizes.
   */
  drawVignette(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    quality: RenderQuality,
  ): void {
    if (!quality.decorations) {
      return;
    }
    const key = `${width}|${height}`;
    if (!this.vignetteGradient || this.vignetteKey !== key) {
      const grad = ctx.createRadialGradient(
        width / 2,
        height / 2,
        Math.max(width, height) * 0.25,
        width / 2,
        height / 2,
        Math.max(width, height) * 0.75,
      );
      grad.addColorStop(0, 'rgba(0, 0, 0, 0)');
      grad.addColorStop(1, `rgba(0, 0, 0, ${VIGNETTE_ALPHA})`);
      this.vignetteGradient = grad;
      this.vignetteKey = key;
    }
    ctx.save();
    ctx.fillStyle = this.vignetteGradient;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }
}

/**
 * Map an engine beat to a 0..1 "thump" intensity that peaks right on
 * each beat boundary and decays as the beat progresses. Used by the
 * strum bar + fret caps to subtly pulse in time with the music.
 *
 * Reduced motion: caller passes 0 strength so the function is never
 * called — there's no need for an internal flag here.
 */
export function beatPulse(beat: number): number {
  const phase = ((beat % 1) + 1) % 1;
  return Math.pow(1 - phase, BEAT_PULSE_DECAY_POWER);
}
